import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  VerificationBadge, AliasBadge, StatusBadge, FreshnessBadge,
  ExclusivityBadge, QualityScoreBadge,
} from '../components/Badges';
import { Search, Filter, MapPin, Lock, Tag, TrendingUp, X, Info, ArrowRight } from 'lucide-react';

export default function MarketplacePage() {
  const [leads, setLeads] = useState([]);
  const [requests, setRequests] = useState([]);
  const [demand, setDemand] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [vocab, setVocab] = useState({ sources: [], exclusivity: [] });
  const [filters, setFilters] = useState({
    exclusivity: '',
    source_type: '',
    max_age_hours: '',
    min_quality_score: '',
    phone_verified: false,
    email_verified: false,
    business_verified: false,
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDemo = !user; // Unauthenticated visitors see the demo preview

  useEffect(() => { fetchVocab(); }, []);
  useEffect(() => { fetchData(); }, [
    filters.exclusivity, filters.source_type, filters.max_age_hours, filters.min_quality_score,
    filters.phone_verified, filters.email_verified, filters.business_verified,
    // Re-fetch when auth resolves so seller/admin Demand Book populates on hard reload
    user?.role,
  ]);

  const fetchVocab = async () => {
    try {
      const res = await api.get('/leads/meta/vocab');
      setVocab(res.data);
    } catch (e) { /* silent */ }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.exclusivity)       params.exclusivity = filters.exclusivity;
      if (filters.source_type)       params.source_type = filters.source_type;
      if (filters.max_age_hours)     params.max_age_hours = filters.max_age_hours;
      if (filters.min_quality_score) params.min_quality_score = filters.min_quality_score;
      if (filters.phone_verified)    params.phone_verified = true;
      if (filters.email_verified)    params.email_verified = true;
      if (filters.business_verified) params.business_verified = true;
      const [leadsRes, requestsRes, demandRes] = await Promise.all([
        api.get('/leads', { params }),
        api.get('/buyer-requests'),
        // Public Demand Book — sellers/admins only. Buyers get 403 and we swallow.
        (user?.role === 'seller' || user?.role === 'admin')
          ? api.get('/demand-profiles/public').catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      setLeads(leadsRes.data);
      setRequests(requestsRes.data);
      setDemand(demandRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const filteredLeads = leads.filter(l =>
    !search || l.category.toLowerCase().includes(search.toLowerCase()) || l.region.toLowerCase().includes(search.toLowerCase())
  );
  const filteredRequests = requests.filter(r =>
    !search || r.category_wanted.toLowerCase().includes(search.toLowerCase())
  );

  const activeFilterCount = Object.values(filters).filter(v => v && v !== '').length;
  const clearFilters = () => setFilters({
    exclusivity: '', source_type: '', max_age_hours: '', min_quality_score: '',
    phone_verified: false, email_verified: false, business_verified: false,
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-medium mb-2">
            Public Marketplace
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">
            Browse verified leads & buyer requests
          </h1>
          <p className="mt-2 text-sm text-gray-600 max-w-2xl">
            Every lead carries a Lead Passport — verified source, quality score, consent, and chain of custody.
          </p>

          {isDemo && (
            <div className="mt-4 flex justify-center sm:justify-start">
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-500/40 bg-amber-50 text-amber-800 rounded-full text-xs font-mono uppercase tracking-[0.15em]"
                data-testid="platform-preview-badge"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                Platform Preview — Early access opening soon
              </span>
            </div>
          )}

          {/* Search + Filters */}
          <div className="mt-6 flex gap-3 max-w-3xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by category or region..."
                className="pl-10 h-11 bg-white"
                data-testid="marketplace-search"
              />
            </div>
            <Button
              variant={showFilters || activeFilterCount ? 'default' : 'outline'}
              onClick={() => setShowFilters(v => !v)}
              className={`h-11 ${showFilters || activeFilterCount ? 'bg-[#0055FF] hover:bg-[#0044CC] text-white' : 'border-gray-300'}`}
              data-testid="filter-btn"
            >
              <Filter className="w-4 h-4 mr-2" /> Filters
              {activeFilterCount > 0 && (
                <span className="ml-2 px-1.5 py-0.5 bg-white text-[#0055FF] rounded text-xs font-mono">{activeFilterCount}</span>
              )}
            </Button>
          </div>

          {isDemo && (
            <div className="mt-2 text-xs italic text-gray-500" data-testid="sample-categories-note">
              Sample categories shown for preview
            </div>
          )}

          {showFilters && (
            <div className="mt-4 max-w-4xl border border-gray-200 bg-white rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="filter-panel">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono mb-1">Exclusivity</div>
                <Select value={filters.exclusivity || 'any'} onValueChange={v => setFilters(f => ({ ...f, exclusivity: v === 'any' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-exclusivity"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {vocab.exclusivity?.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono mb-1">Lead Source</div>
                <Select value={filters.source_type || 'any'} onValueChange={v => setFilters(f => ({ ...f, source_type: v === 'any' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-source"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {vocab.sources?.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono mb-1">Max Age</div>
                <Select value={filters.max_age_hours || 'any'} onValueChange={v => setFilters(f => ({ ...f, max_age_hours: v === 'any' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-age"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="1">Under 1 hour</SelectItem>
                    <SelectItem value="24">Under 24 hours</SelectItem>
                    <SelectItem value="168">Under 7 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono mb-1">Min Quality Score</div>
                <Select value={filters.min_quality_score || 'any'} onValueChange={v => setFilters(f => ({ ...f, min_quality_score: v === 'any' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-quality"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="60">60+</SelectItem>
                    <SelectItem value="75">75+</SelectItem>
                    <SelectItem value="85">85+</SelectItem>
                    <SelectItem value="90">90+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 md:col-span-4 flex flex-wrap items-center gap-4 pt-2">
                <label className="inline-flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={filters.phone_verified} onChange={e => setFilters(f => ({ ...f, phone_verified: e.target.checked }))} data-testid="filter-phone-verified" />
                  Phone verified
                </label>
                <label className="inline-flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={filters.email_verified} onChange={e => setFilters(f => ({ ...f, email_verified: e.target.checked }))} data-testid="filter-email-verified" />
                  Email verified
                </label>
                <label className="inline-flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={filters.business_verified} onChange={e => setFilters(f => ({ ...f, business_verified: e.target.checked }))} data-testid="filter-business-verified" />
                  Business verified
                </label>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-xs" data-testid="clear-filters-btn">
                    <X className="w-3 h-3 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <Tabs defaultValue="leads" className="w-full">
          <TabsList className="bg-gray-100 p-1" data-testid="marketplace-tabs">
            <TabsTrigger value="leads" className="data-[state=active]:bg-white" data-testid="tab-leads">
              <Tag className="w-4 h-4 mr-2" />
              Lead Supply
              <span className="ml-2 px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono" data-testid="tab-leads-count">
                {isDemo ? `≈${filteredLeads.length}` : filteredLeads.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="requests" className="data-[state=active]:bg-white" data-testid="tab-requests">
              <TrendingUp className="w-4 h-4 mr-2" />
              Buyer Requests
              <span className="ml-2 px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono" data-testid="tab-requests-count">
                {isDemo ? `≈${filteredRequests.length}` : filteredRequests.length}
              </span>
            </TabsTrigger>
            {(user?.role === 'seller' || user?.role === 'admin') && (
              <TabsTrigger value="demand" className="data-[state=active]:bg-white" data-testid="tab-demand">
                <TrendingUp className="w-4 h-4 mr-2" />
                Demand Book
                <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-[#0044CC] rounded text-xs font-mono" data-testid="tab-demand-count">
                  {demand.length}
                </span>
              </TabsTrigger>
            )}
          </TabsList>

          {isDemo && (
            <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-500" data-testid="sample-data-note">
              <Info className="w-3 h-3" />
              <span className="italic">Sample data shown for demonstration — real inventory unlocks after waitlist onboarding</span>
            </div>
          )}

          <TabsContent value="leads" className="mt-6">
            {loading ? (
              <div className="text-center py-16 text-gray-500 text-sm font-mono">Loading marketplace...</div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-16 text-gray-500 text-sm">No leads match your filters</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="leads-grid">
                {filteredLeads.map((lead) => (
                  <div
                    key={lead.lead_id}
                    onClick={() => {
                      if (isDemo) setPreviewOpen(true);
                      else navigate(`/leads/${lead.lead_id}`);
                    }}
                    className={`relative bg-white border rounded-lg p-5 cursor-pointer card-hover ${
                      isDemo ? 'border-amber-500/25' : 'border-gray-200'
                    }`}
                    data-testid={`lead-card-${lead.lead_id}`}
                  >
                    {isDemo && (
                      <div
                        className="absolute -top-px -right-px overflow-hidden w-24 h-24 pointer-events-none"
                        data-testid="lead-card-demo-ribbon"
                        aria-hidden="true"
                      >
                        <div className="absolute top-[18px] right-[-30px] rotate-45 bg-amber-500 text-white text-[10px] font-mono font-semibold tracking-[0.2em] px-9 py-1 shadow-sm">
                          DEMO
                        </div>
                      </div>
                    )}

                    <div className="flex items-start justify-between mb-3">
                      <AliasBadge alias={lead.seller_alias} />
                      {isDemo ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border border-amber-500/40 bg-amber-50 text-amber-800"
                          data-testid="lead-status-preview"
                        >
                          Preview
                        </span>
                      ) : (
                        <StatusBadge status={lead.status} />
                      )}
                    </div>

                    <div className="font-mono text-xs text-gray-400 mb-2">{lead.lead_id}</div>
                    <h3 className="font-heading text-lg font-semibold text-gray-900 mb-3">{lead.category}</h3>

                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {lead.exclusivity && <ExclusivityBadge exclusivity={lead.exclusivity} sharedCap={lead.shared_cap} size="sm" />}
                      {typeof lead.quality_score === 'number' && <QualityScoreBadge score={lead.quality_score} size="sm" />}
                      {!isDemo && <FreshnessBadge hours={lead.lead_age_hours} />}
                    </div>

                    <div className="space-y-1.5 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="w-3.5 h-3.5 text-gray-400" />
                        <span>{lead.region}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 text-xs">
                        <span className="text-gray-400 uppercase tracking-wider font-mono">Source</span>
                        <span className="capitalize">{(lead.source_type || '').replace(/_/g, ' ')}</span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                      <div className="flex items-baseline gap-1.5">
                        <div className="font-mono text-lg font-semibold text-gray-900">${lead.price.toFixed(2)}</div>
                        {isDemo && (
                          <span className="text-[10px] font-mono uppercase tracking-wider text-amber-700">
                            preview price
                          </span>
                        )}
                      </div>
                      <VerificationBadge level={lead.verification_level} />
                    </div>

                    {isDemo ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); setPreviewOpen(true); }}
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 h-10 border border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-900 rounded-md text-sm font-medium transition-colors"
                        data-testid={`preview-lead-btn-${lead.lead_id}`}
                      >
                        Preview Lead
                      </button>
                    ) : (
                      <div className="mt-3 flex items-center gap-1 text-xs text-gray-400">
                        <Lock className="w-3 h-3" />
                        Contact unlocks after purchase
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="requests" className="mt-6">
            {loading ? (
              <div className="text-center py-16 text-gray-500 text-sm font-mono">Loading requests...</div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-16 text-gray-500 text-sm">No active buyer requests</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="requests-grid">
                {filteredRequests.map((req) => (
                  <div
                    key={req.request_id}
                    className={`relative bg-white border rounded-lg p-5 card-hover ${
                      isDemo ? 'border-amber-500/25' : 'border-gray-200'
                    }`}
                    data-testid={`request-card-${req.request_id}`}
                  >
                    {isDemo && (
                      <div className="absolute -top-px -right-px overflow-hidden w-24 h-24 pointer-events-none" aria-hidden="true">
                        <div className="absolute top-[18px] right-[-30px] rotate-45 bg-amber-500 text-white text-[10px] font-mono font-semibold tracking-[0.2em] px-9 py-1 shadow-sm">
                          DEMO
                        </div>
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-3">
                      <AliasBadge alias={req.buyer_alias} />
                      {isDemo ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border border-amber-500/40 bg-amber-50 text-amber-800">
                          Preview
                        </span>
                      ) : (
                        <StatusBadge status={req.status} />
                      )}
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-gray-900 mb-3">Wants: {req.category_wanted}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="text-gray-900 font-medium">{req.location}</span></div>
                      <div className="flex justify-between"><span className="text-gray-500">Volume</span><span className="text-gray-900 font-medium font-mono">{req.volume_per_week}/week</span></div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Target Price</span>
                        <span className="text-gray-900 font-mono font-semibold">
                          ${req.target_price.toFixed(2)}
                          {isDemo && <span className="ml-1.5 text-[10px] text-amber-700 uppercase tracking-wider">preview</span>}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Criteria</div>
                      <div className="text-sm text-gray-700">{req.criteria}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {(user?.role === 'seller' || user?.role === 'admin') && (
            <TabsContent value="demand" className="mt-6" data-testid="demand-tab-content">
              {demand.length === 0 ? (
                <div className="text-center py-16 text-gray-500 text-sm">
                  No active buyer demand yet. When buyers post Demand Profiles they will appear here anonymised.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="demand-grid">
                  {demand.map(p => (
                    <div
                      key={p.profile_id}
                      className="bg-white border border-gray-200 rounded-lg p-5 hover:border-[#0055FF] transition"
                      data-testid={`demand-card-${p.profile_id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="text-xs uppercase tracking-wider text-[#0055FF] font-mono font-medium mb-1">
                            {p.vertical} — {p.category}
                          </div>
                          <div className="font-heading text-lg font-semibold text-gray-900" data-testid={`demand-title-${p.profile_id}`}>
                            {(p.buyer_alias || 'Anon')} Buyer
                          </div>
                        </div>
                        <span className="font-mono text-xs px-2 py-0.5 rounded bg-blue-50 text-[#0044CC] border border-blue-200">
                          {p.remaining_this_month} left
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-sm mb-3">
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-mono">Volume</div>
                          <div className="font-semibold text-gray-900">{p.monthly_volume}/mo</div>
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-mono">Target CPL</div>
                          <div className="font-semibold text-gray-900">${p.target_cpl}</div>
                        </div>
                        <div>
                          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-mono">Max CPL</div>
                          <div className="font-semibold text-gray-900">${p.max_cpl}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                        <div><span className="text-gray-500">Geo:</span> {(p.geography || []).join(', ')}</div>
                        <div><span className="text-gray-500">Exclusivity:</span> {p.exclusivity}</div>
                        {p.max_lead_age_hours && <div><span className="text-gray-500">Max age:</span> {p.max_lead_age_hours}h</div>}
                        {(p.verification_required || []).length > 0 && (
                          <div><span className="text-gray-500">Verify:</span> {p.verification_required.join(', ')}</div>
                        )}
                      </div>
                      {p.qualification_criteria && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="text-xs text-gray-500 mb-1 uppercase tracking-wider">Qualification</div>
                          <div className="text-sm text-gray-700">{p.qualification_criteria}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          )}
        </Tabs>

        {isDemo && (
          <section
            className="mt-16 bg-gray-900 text-white rounded-lg p-8 sm:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
            data-testid="waitlist-cta-block"
          >
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-amber-400 mb-2">
                Real inventory · Limited spots
              </div>
              <h3 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
                Want access to live leads?
              </h3>
              <p className="mt-2 text-sm text-gray-300 max-w-2xl">
                We're onboarding 20 founding buyers and 15 sellers. Join the waitlist to lock in beta pricing.
              </p>
            </div>
            <button
              onClick={() => { navigate('/'); setTimeout(() => document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' }), 50); }}
              className="inline-flex items-center gap-2 h-11 px-6 bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-md text-sm font-medium transition-colors flex-shrink-0"
              data-testid="cta-join-waitlist"
            >
              Join the Waitlist
              <ArrowRight className="w-4 h-4" />
            </button>
          </section>
        )}
      </div>

      {/* Demo preview modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent data-testid="preview-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Marketplace opens soon</DialogTitle>
            <DialogDescription className="pt-2 text-gray-600">
              You're viewing a platform preview. Live purchasing unlocks after your account is
              verified. Join the waitlist and we'll notify you the moment real inventory goes live.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 gap-2">
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
              data-testid="preview-modal-close"
            >
              Keep browsing
            </Button>
            <Button
              onClick={() => { setPreviewOpen(false); navigate('/'); setTimeout(() => document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' }), 50); }}
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
              data-testid="preview-modal-join-waitlist"
            >
              Join the Waitlist
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
